import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PROFUMI_IMGS } from "../constants";
import { useLang } from "../i18n";
import { useQuote } from "../quote";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.95, ease: [0.22, 1, 0.36, 1] } }),
};

const TILES = [
  {
    slug: "imperatrice-edp",
    name: "Imperatrice — Eau de Parfum (Femme)",
    img: PROFUMI_IMGS.femme,
    subKey: "femme_sub",
    cap: "IMPERATRICE · EAU DE PARFUM POUR FEMME",
    cls: "lg:col-span-5",
    imgH: "h-[440px] md:h-[620px]",
    cta: true,
  },
  {
    slug: "imperivm-edp",
    name: "Imperivm — Eau de Parfum (Homme)",
    img: PROFUMI_IMGS.homme,
    subKey: "homme_sub",
    cap: "IMPERIVM · EAU DE PARFUM POUR HOMME — IMPERIUM · HONOR · FIDELITAS",
    cls: "lg:col-span-7",
    imgH: "h-[440px] md:h-[620px]",
    cta: true,
  },
  {
    slug: "legionario-edp",
    name: "Legionario — Eau de Parfum (Homme)",
    img: PROFUMI_IMGS.legionario,
    subKey: "homme_sub",
    cap: "LEGIONARIO · EAU DE PARFUM POUR HOMME — IMPERIUM · HONOR · FIDELITAS",
    cls: "lg:col-span-5",
    imgH: "h-[420px] md:h-[580px]",
    cta: true,
  },
  {
    slug: "imperatrice-gems",
    name: "Imperatrice — Edizione Gioiello",
    img: PROFUMI_IMGS.gems,
    cap: "III ROMA · TRADITIO AETERNA — POUR FEMME",
    cls: "lg:col-span-7",
    imgH: "h-[420px] md:h-[580px]",
    cta: false,
  },
];

const BANNERS = [
  { key: "grid", img: PROFUMI_IMGS.grid, alt: "Imperatrice Collection" },
  { key: "grid_homme", img: PROFUMI_IMGS.hommeGrid, alt: "Imperivm Collection" },
];

export default function Profumi() {
  const { t } = useLang();
  const { add, openDrawer } = useQuote();

  return (
    <section id="profumi" data-testid="profumi" className="bg-obsidian relative px-6 py-28 md:px-12 md:py-40">
      <div className="relative mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-[0.65rem] uppercase tracking-[0.5em] text-gold"
          >
            {t("profumi.eyebrow")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-carved-gold font-display mt-6 text-4xl leading-tight md:text-6xl"
          >
            {t("profumi.title")}
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="max-w-md text-lg leading-relaxed text-parchment/85"
        >
          {t("profumi.sub")}
        </motion.p>
      </div>

      <motion.div
        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.08 }}
        className="relative grid gap-x-6 gap-y-12 lg:grid-cols-12"
      >
        {TILES.map((tile, i) => (
          <motion.div
            key={tile.slug}
            variants={fadeUp}
            custom={i}
            data-testid={`profumi-tile-${tile.slug}`}
            className={tile.cls}
          >
            <div className={`clip-frame-sm group relative overflow-hidden border border-gold/20 ${tile.imgH}`}>
              <img
                src={tile.img}
                alt={tile.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
              />
            </div>
            <div className="mt-5">
              <p className="text-[0.65rem] uppercase tracking-[0.3em] text-gold md:text-[0.72rem]">{tile.cap}</p>
              {tile.cta && (
                <>
                  <p className="mt-3 max-w-[52ch] text-sm leading-relaxed text-parchment/85 md:text-base">
                    {t(`profumi.${tile.subKey}`)}
                  </p>
                  <button
                    data-testid={`add-to-request-${tile.slug}`}
                    onClick={() => {
                      add({ slug: tile.slug, name: tile.name, price: "—" });
                      openDrawer();
                    }}
                    className="btn-gold mt-5 !px-6 !py-3 !text-[0.68rem]"
                  >
                    {t("products.add")} <ArrowRight size={14} className="inline" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        ))}

        {BANNERS.map((b, i) => (
          <motion.div
            key={b.key}
            variants={fadeUp}
            custom={4 + i}
            data-testid={`profumi-${b.key}-banner`}
            className="lg:col-span-6"
          >
            <div className="clip-frame-sm group relative h-[300px] overflow-hidden border border-gold/20 md:h-[400px]">
              <img
                src={b.img}
                alt={b.alt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
            </div>
            <p className="mt-5 text-[0.65rem] uppercase tracking-[0.35em] text-gold md:text-[0.72rem]">{t(`profumi.${b.key}`)}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}
